import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation, Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { api, ApiError } from "../lib/api";
import { useAuthStore } from "../store/auth";
import {
  loadUnitProgress,
  saveUnitProgress,
  clearUnitProgress,
  restoreTasks,
  savePendingUnit,
  isRetryableError,
  type UnitProgress,
} from "../lib/unit-progress";
import { SakuraField } from "../components/SakuraField";
import { ReportModal } from "../components/ReportModal";
import { CelebrationVideo } from "../components/animation/CelebrationVideo";
import { SakuraCoinIcon, SparkleStarIcon } from "../components/animation/AnimatedUIIcon";
import { AnimalBubble } from "../components/dialog/AnimalBubble";
import { AREA_BY_ID, AREA_MOTIF_ICONS, getAreaName, type AreaId } from "../content/map";
import {
  localAnswer,
  buildUnit,
  type Section,
  type Task,
  type UnitResult,
  type LessonExplanation,
} from "../content/lesson";

// 各エリア完了時のお祝いセリフ（どうぶつ住民の口調）
const RESIDENT_CONGRATS: Record<AreaId, { ja: string; en: string }> = {
  airport: { ja: "クルックー！大阪へようこそ！自分の名前、バッチリ伝わったよ！", en: "Coo! Welcome to Osaka! You introduced yourself perfectly!" },
  home: { ja: "のんびりよくできたねぇ。これで部屋のものもバッチリだね！", en: "Well done, take it easy! Now you know the things in your room!" },
  school: { ja: "ホッホー！すばらしい！これで教室で質問もばっちりですぞ！", en: "Hoo-hoo! Magnificent! You're ready to ask questions in class!" },
  shop: { ja: "やったポン！コンビニでの買い物はこれで完璧だポン！", en: "Yay pon! Shopping at the shop is super easy now pon!" },
  cafe: { ja: "ニャー！これでいつでも一緒にお茶できるニャ！", en: "Meow! Perfect! Now we can have a coffee break anytime meow!" },
  station: { ja: "ワン！電車の行き先や乗り換えもこれで迷わないワン！", en: "Woof! Travel directions and train lines are no problem now!" },
  library: { ja: "メェ〜、よくできました。丁寧にお願いや許可を言えたね", en: "Baa~, lovely. You made polite requests beautifully." },
  park: { ja: "キキッ！いいね！過去の出来事を話すと会話がもっと弾むよ！", en: "Chirp! Talking about yesterday makes friendship grow faster!" },
  friendhouse: { ja: "ガオー！…じゃなくて、好きなことを話してくれて嬉しいよ！", en: "Roar! Just kidding, so glad you shared what you like!" },
  hospital: { ja: "安心しましたよ。具合が悪いときもちゃんと伝えられるね", en: "What a relief. You can explain clearly whenever you need care." },
  cityoffice: { ja: "ペタペタ…すごい！難しい手続きも理由をしっかり説明できました！", en: "Waddle-waddle... Amazing! You handled the paperwork with clarity!" },
  workplace: { ja: "よーし！お疲れさま！仕事の報告も自分の意見も立派に伝わったぞ！", en: "All right! Great teamwork! Your work updates were crystal clear!" },
};

const MAX_HEARTS = 5;
const UNITS_PER_SECTION = 5;

function shuffle<T>(arr: T[]): T[] {
  return [...arr].sort(() => Math.random() - 0.5);
}

type Feedback = { is_correct: boolean; correct_answer: string };

// ユニットプレイヤー（/unit/:sectionId/:n）：スキマ時間サイズの問題セット（約5問）。
// セクションのプールからユニットの問題を抽出し、Duolingo 風 UX でこなす。
// ユニットが変わったら（「つぎのユニットへ」等）画面ごと作り直し、そのユニットの途中経過を読み直す
export function UnitPage() {
  const { sectionId, n } = useParams();
  return <UnitLesson key={`${sectionId}/${n}`} />;
}

function UnitLesson() {
  const { t, i18n } = useTranslation(["common", "learner"]);
  const isJa = i18n.language === "ja";
  const { sectionId, n } = useParams();
  const unitIndex = Number(n) || 1;
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id ?? null);

  const { data: section, isError, error } = useQuery({
    queryKey: ["section", sectionId],
    queryFn: () => api<Section>(`/api/learner/sections/${sectionId}`),
    enabled: !!sectionId,
    retry: false,
  });

  // 前回の途中経過（戻る・閉じる・通信切れで中断した分）
  const [saved, setSaved] = useState<UnitProgress | null>(() =>
    userId && sectionId ? loadUnitProgress(userId, sectionId, unitIndex) : null,
  );

  const [index, setIndex] = useState(() =>
    saved ? Math.min(saved.answers.length, saved.taskIds.length - 1) : 0,
  );
  const [choiceSel, setChoiceSel] = useState<string | null>(null);
  const [builtKeys, setBuiltKeys] = useState<number[]>([]);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [answers, setAnswers] = useState<Array<{ task_id: string; selected: string }>>(() => saved?.answers ?? []);
  const [hearts, setHearts] = useState(() => saved?.hearts ?? MAX_HEARTS);
  const [refillOpen, setRefillOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [quitOpen, setQuitOpen] = useState(false);
  const [streak, setStreak] = useState(() => saved?.streak ?? 0);
  const [correctCount, setCorrectCount] = useState(() => saved?.correctCount ?? 0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<UnitResult | null>(null);
  const [runId, setRunId] = useState(0);
  const [explanationDone, setExplanationDone] = useState(() => saved?.explanationDone ?? false);

  // ユニットの問題（プールからランダム抽出。runId で再抽選。前回の途中経過があれば同じ問題を復元）
  const restored = useMemo(
    () => (section && saved ? restoreTasks(section, saved.taskIds) : null),
    [section, saved],
  );
  const tasks = useMemo(() => {
    if (!section) return [];
    if (restored) return restored;
    return buildUnit(section, unitIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, unitIndex, runId, restored]);
  const task = tasks[index];

  // 教材が更新され前回の問題が復元できない時は、途中経過を捨てて最初から
  useEffect(() => {
    if (section && saved && !restored) restart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, saved, restored]);

  // 1問ごとに途中経過を保存する
  useEffect(() => {
    if (!userId || !sectionId || !section || tasks.length === 0 || result) return;
    if (saved && !restored) return; // 教材が変わり作り直す途中。古い答えと新しい問題を混ぜて書かない
    if (answers.length === 0 && !explanationDone) return;
    saveUnitProgress(userId, sectionId, unitIndex, {
      taskIds: tasks.map((t) => t.id),
      answers,
      hearts,
      streak,
      correctCount,
      explanationDone,
      savedAt: Date.now(),
    });
  }, [userId, sectionId, section, unitIndex, tasks, saved, restored, answers, hearts, streak, correctCount, explanationDone, result]);

  // 前回、全問答えたのに完了の送信ができていなければ、開いた時に送り直す
  const resubmitTried = useRef(false);
  useEffect(() => {
    if (resubmitTried.current || !restored || result || submitting) return;
    if (answers.length >= tasks.length && tasks.length > 0) {
      resubmitTried.current = true;
      void submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restored, tasks.length]);

  // 解説はセクション最初のユニット（U1）でのみ表示
  const showsExplanation = unitIndex === 1 && !!section?.explanation;

  // 学習中のブラウザの「戻る」（Android の戻るキーを含む）を受け止める。
  // 学習画面の上に同じURLの「見張り」の履歴を1つ積む。戻るで見張りが外れたら確認を出して積み直す。
  // 1問も答えていなければそのまま前の画面へ戻す
  const inLesson = !!section && !!task && !result && (!showsExplanation || explanationDone);
  const onGuardEntry = !!(location.state as { unitGuard?: boolean } | null)?.unitGuard;
  const guardArmed = useRef(false); // 見張りを積んだ後か
  const guardPushedFrom = useRef<string | null>(null); // StrictMode の二重実行で2回積まない
  const leaving = useRef(false); // やめる操作中（見張りを外した後にエリアへ差し替える）
  const areaPath = `/area/${section?.area_id ?? ""}`;

  function backOut() {
    // 直接開いた（前の履歴が無い）時はアプリの外へ出さずエリアへ
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate(areaPath, { replace: true });
  }

  useEffect(() => {
    if (leaving.current) {
      // 見張りを外し終えたら、学習画面の履歴をエリアに差し替える（戻るでレッスンに戻らない）
      if (!onGuardEntry) navigate(areaPath, { replace: true });
      return;
    }
    if (!inLesson) {
      // 結果画面では見張りが要らない。残すと戻るが空振りするので外す
      if (onGuardEntry && result) navigate(-1);
      guardArmed.current = false;
      return;
    }
    if (onGuardEntry) {
      // 見張りの上にいる（積んだ直後・再読み込み後・エリアから進むで戻ってきた時）
      guardArmed.current = true;
      guardPushedFrom.current = null;
      return;
    }
    if (guardPushedFrom.current === location.key) return;
    if (guardArmed.current) {
      // 戻るで見張りが外れた
      guardArmed.current = false;
      if (answers.length === 0) {
        backOut();
        return;
      }
      setQuitOpen(true);
    }
    guardPushedFrom.current = location.key;
    navigate(location.pathname, { state: { unitGuard: true } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inLesson, location.key]);

  function leaveToArea() {
    setQuitOpen(false);
    if (onGuardEntry) {
      leaving.current = true;
      navigate(-1); // 見張りを外す → 上の effect がエリアへ差し替える
    } else {
      navigate(areaPath, { replace: true });
    }
  }

  function requestQuit() {
    if (answers.length === 0) leaveToArea();
    else setQuitOpen(true);
  }

  const choices = useMemo(() => {
    if (!task || task.type === "word_order") return [];
    return shuffle(task.choices);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id, runId]);

  const bank = useMemo(() => {
    if (!task || task.type !== "word_order") return [] as Array<{ key: number; label: string }>;
    const items = [...task.tokens, ...task.distractors].map((label, key) => ({ key, label }));
    return shuffle(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id, runId]);

  function resetTaskState() {
    setChoiceSel(null);
    setBuiltKeys([]);
    setFeedback(null);
  }

  function restart() {
    if (userId && sectionId) clearUnitProgress(userId, sectionId, unitIndex);
    setSaved(null);
    resubmitTried.current = true;
    setIndex(0);
    resetTaskState();
    setAnswers([]);
    setHearts(MAX_HEARTS);
    setRefillOpen(false);
    setStreak(0);
    setCorrectCount(0);
    setResult(null);
    setRunId((r) => r + 1);
    setExplanationDone(false);
  }

  // ユニット切り替え時の状態リセット（ハートを MAX_HEARTS に戻す等）は、UnitPage の key で画面ごと作り直して行う

  function labelOf(key: number): string {
    return bank.find((b) => b.key === key)?.label ?? "";
  }

  function currentSelected(): string {
    if (!task) return "";
    if (task.type === "word_order") return builtKeys.map(labelOf).join(" ");
    return choiceSel ?? "";
  }

  const canCheck = (() => {
    if (!task || feedback || submitting) return false;
    if (task.type === "word_order") return builtKeys.length > 0;
    return !!choiceSel;
  })();

  function grade(selected: string): boolean {
    if (!task) return false;
    return selected.trim() === localAnswer(task).trim();
  }

  function check() {
    if (!task || feedback || submitting || !canCheck) return;
    const selected = currentSelected();
    const ok = grade(selected);
    setFeedback({ is_correct: ok, correct_answer: localAnswer(task) });
    setAnswers((a) => [...a, { task_id: task.id, selected }]);
    if (ok) {
      setCorrectCount((c) => c + 1);
      setStreak((s) => s + 1);
    } else {
      setStreak(0);
      setHearts((h) => Math.max(0, h - 1));
    }
  }

  function skip() {
    if (!task || feedback || submitting) return;
    setFeedback({ is_correct: false, correct_answer: localAnswer(task) });
    setAnswers((a) => [...a, { task_id: task.id, selected: "" }]);
    setStreak(0);
  }

  async function next() {
    if (!section || !task) return;
    if (index + 1 < tasks.length) {
      resetTaskState();
      setIndex((i) => i + 1);
      return;
    }
    await submit();
  }

  // 最終問題 → サーバー採点・報酬・解放。成功した時だけ途中経過を消す
  // （失敗時は残し、次に開いた時に送り直す）
  async function submit() {
    if (!section) return;
    setSubmitting(true);
    try {
      const res = await api<UnitResult>(
        `/api/learner/units/${section.id}-u${unitIndex}/complete`,
        { method: "POST", body: { answers } },
      );
      if (userId && sectionId) clearUnitProgress(userId, sectionId, unitIndex);
      setResult(res);
      void queryClient.invalidateQueries({ queryKey: ["profile"] });
      void queryClient.invalidateQueries({ queryKey: ["areas"] });
      void queryClient.invalidateQueries({ queryKey: ["area", section.area_id] });
      void queryClient.invalidateQueries({ queryKey: ["section", section.id] });
    } catch (err) {
      console.error("Unit submit failed:", err);
      // 通信切れ等は答えを送信待ちに移し、次に地図やユニットを開いた時に送る。
      // 入力エラー（4xx）は送り直しても通らないので、途中経過ごと捨てる（開くたびに再送し続けない）
      if (userId && sectionId) {
        if (isRetryableError(err)) savePendingUnit(userId, `${section.id}-u${unitIndex}`, answers);
        clearUnitProgress(userId, sectionId, unitIndex);
      }
      setResult(buildLocalUnitResult(section, unitIndex, answers, correctCount));
    } finally {
      setSubmitting(false);
    }
  }

  function refill() {
    setHearts(MAX_HEARTS);
    setRefillOpen(false);
  }

  // キーボード操作（PC）：1〜4で選択、Enterで確定/次、SpaceでSKIP
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (refillOpen || reportOpen || quitOpen || result || !task) return;
      if (showsExplanation && !explanationDone) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.key === "Enter") {
        e.preventDefault();
        if (feedback) void next();
        else if (canCheck) check();
        return;
      }
      if (e.key === " " || e.code === "Space") {
        e.preventDefault();
        if (!feedback) skip();
        return;
      }
      if (task.type !== "word_order" && !feedback) {
        const num = Number(e.key);
        if (Number.isInteger(num) && num >= 1 && num <= choices.length) {
          e.preventDefault();
          setChoiceSel(choices[num - 1]);
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id, feedback, canCheck, choices, refillOpen, reportOpen, quitOpen, result, explanationDone, showsExplanation]);

  if (result && section) {
    return <UnitComplete result={result} section={section} unitIndex={unitIndex} onRetry={restart} />;
  }

  // 未開放のエリアのユニットを URL で直接開いた時はマップへ戻す（API は 403）
  if (isError && error instanceof ApiError && error.status === 403) {
    return <Navigate to="/" replace />;
  }
  if (isError) {
    return (
      <div className="w-full min-h-screen flex flex-col items-center justify-center px-6 text-center bg-gradient-to-br from-sakura/10 via-white to-blue-50">
        <p className="text-muted text-lg mb-4">{t("learner:lesson_load_error")}</p>
        <Link to="/" className="bg-primary text-white font-bold rounded-2xl px-6 py-3 shadow">
          {t("learner:back_to_map")}
        </Link>
      </div>
    );
  }

  if (!section || !task) {
    return <div className="p-8 text-center text-muted text-xl">{t("common:loading")}</div>;
  }

  // 解説（Skill Card）— セクション最初のユニットのみ
  if (showsExplanation && !explanationDone && section.explanation) {
    return (
      <ExplanationScreen
        explanation={section.explanation}
        titleJa={section.title_ja}
        titleEn={section.title_en}
        onStart={() => setExplanationDone(true)}
        onExit={() => navigate(`/area/${section.area_id}`)}
      />
    );
  }

  const progressPct = Math.round(((index + 1) / tasks.length) * 100);

  return (
    <div className="relative w-full h-screen overflow-hidden bg-gradient-to-br from-sakura/10 via-white to-blue-50">
      <SakuraField />

      <header className="absolute top-0 left-0 right-0 z-30 p-3 sm:p-6">
        <div className="max-w-3xl mx-auto relative">
          <AnimatePresence>
            {streak >= 2 && (
              <motion.div
                key={`streak-${streak}`}
                initial={{ opacity: 0, y: 6, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.25 }}
                className="absolute -top-4 sm:-top-5 left-1/2 -translate-x-1/2 text-[10px] sm:text-xs font-bold tracking-wide text-yellow-600 whitespace-nowrap pointer-events-none"
              >
                {t("learner:streak_in_row", { n: streak })}
              </motion.div>
            )}
          </AnimatePresence>
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={requestQuit}
              aria-label={t("common:close")}
              className="bg-white/90 backdrop-blur rounded-full shadow-lg w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-primary text-lg sm:text-xl font-bold hover:bg-white shrink-0"
            >
              ×
            </button>
            <div className="flex-1 h-3 sm:h-4 bg-white/60 rounded-full overflow-hidden shadow-inner">
              <motion.div
                className={`h-full ${streak >= 2 ? "bg-gradient-to-r from-yellow-300 to-yellow-500" : "bg-gradient-to-r from-sakura to-primary"}`}
                initial={{ width: 0 }}
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              />
            </div>
            <button
              type="button"
              onClick={() => setRefillOpen(true)}
              aria-label="ハート残数と応援"
              className={`backdrop-blur rounded-xl sm:rounded-2xl shadow-lg px-2.5 sm:px-4 py-1.5 sm:py-2 flex items-center gap-1.5 sm:gap-2 shrink-0 transition active:scale-95 ${hearts > 0
                  ? "bg-white/90 hover:bg-white text-accent"
                  : "bg-amber-50/95 border border-amber-300/60 text-amber-700"
                }`}
            >
              <HeartIcon filled={hearts > 0} />
              {hearts > 0 ? (
                <span className="text-base sm:text-lg font-bold text-accent">{hearts}</span>
              ) : (
                <span className="text-xs sm:text-sm font-black text-amber-700 whitespace-nowrap">
                  {isJa ? "💛 フリー挑戦中" : i18n.language === "id" ? "💛 Bebas Coba" : "💛 Free Mode"}
                </span>
              )}
            </button>
          </div>
          {/* エリアの生活モチーフ表示（絵文字アイコン） */}
          {(() => {
            const mapArea = AREA_BY_ID[section.area_id as AreaId];
            if (!mapArea) return null;
            const areaName = getAreaName(mapArea, i18n.language);
            const motifIcons = AREA_MOTIF_ICONS[section.area_id as AreaId] ?? ["📍"];
            return (
              <div className="flex items-center justify-between text-[10px] sm:text-xs text-stone-500 font-bold px-1 mt-1 select-none">
                <span className="flex items-center gap-1.5">
                  <span>{mapArea.residentEmoji}</span>
                  <span className="text-[#8f332c]">{areaName}</span>
                </span>
                <span className="flex items-center gap-1.5 opacity-90 text-sm">
                  {motifIcons.map((icon, idx) => (
                    <span key={idx} className="hover:scale-110 transition drop-shadow-xs">{icon}</span>
                  ))}
                </span>
              </div>
            );
          })()}
        </div>
      </header>

      <div className="relative z-20 flex flex-col items-center h-full px-4 sm:px-6 pt-20 sm:pt-28 pb-28 sm:pb-32 overflow-y-auto">
        <div className="w-full max-w-2xl mx-auto my-auto">
          <p className="text-sm sm:text-lg text-muted mb-3 sm:mb-5 text-center">
            {t(`learner:instr_${task.type}`)}
          </p>

          {task.type === "vocab" && (
            <VocabView task={task} choices={choices} selected={choiceSel} feedback={feedback} onPick={setChoiceSel} />
          )}
          {task.type === "translate" && (
            <TranslateView task={task} choices={choices} selected={choiceSel} feedback={feedback} onPick={setChoiceSel} />
          )}
          {task.type === "word_order" && (
            <WordOrderView
              task={task}
              bank={bank}
              builtKeys={builtKeys}
              feedback={feedback}
              labelOf={labelOf}
              onAdd={(k) => !feedback && setBuiltKeys((b) => [...b, k])}
              onRemove={(k) => !feedback && setBuiltKeys((b) => b.filter((x) => x !== k))}
            />
          )}
          {task.type === "fill_blank" && (
            <FillBlankView task={task} choices={choices} selected={choiceSel} feedback={feedback} onPick={setChoiceSel} />
          )}
        </div>
      </div>

      <div
        className={`absolute bottom-0 left-0 right-0 z-30 border-t transition-colors ${feedback
            ? feedback.is_correct
              ? "border-green-200 bg-green-50/95"
              : "border-orange-200 bg-orange-50/95"
            : "border-gray-200 bg-white/95"
          } backdrop-blur pb-[env(safe-area-inset-bottom)]`}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3.5 sm:py-4 flex items-center justify-between gap-3 sm:gap-4 min-h-[92px] sm:min-h-[104px]">
          {feedback ? (
            <FeedbackBar
              feedback={feedback}
              submitting={submitting}
              isLast={index + 1 >= tasks.length}
              onNext={next}
              onReport={() => setReportOpen(true)}
            />
          ) : (
            <>
              <button
                onClick={skip}
                disabled={submitting}
                className="border-2 border-gray-300 text-stone-500 font-bold rounded-2xl px-5 sm:px-8 py-3.5 hover:bg-gray-50 active:scale-95 disabled:opacity-50 text-sm sm:text-base min-w-[90px]"
              >
                SKIP
              </button>
              <button
                onClick={check}
                disabled={!canCheck}
                className={`font-black rounded-2xl px-8 sm:px-12 py-3.5 shadow-lg active:scale-98 transition text-sm sm:text-base min-w-[130px] ${canCheck ? "bg-primary text-white hover:bg-primary/90 shadow-primary/25" : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
              >
                CHECK
              </button>
            </>
          )}
        </div>
      </div>

      <ReportModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        areaId={section.area_id}
        taskId={task.id}
        context={{ selected: currentSelected(), correct: localAnswer(task), is_correct: feedback?.is_correct ?? null }}
      />

      <AnimatePresence>
        {refillOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.88, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.88, opacity: 0 }}
              className="bg-white rounded-3xl shadow-2xl px-6 sm:px-8 py-8 max-w-sm w-full text-center border border-[#d9b45f]/30"
            >
              <div className="mx-auto mb-3 w-fit text-4xl">
                🐰
              </div>
              <h2 className="text-lg sm:text-xl font-black text-primary mb-1.5">
                {isJa
                  ? "だいじょうぶ、まちがえてもええんやで！🐾"
                  : i18n.language === "id"
                    ? "Tidak apa-apa, salah itu wajar!🐾"
                    : "No worries, mistakes help you learn!🐾"}
              </h2>
              <p className="text-xs sm:text-sm text-stone-600 mb-6 leading-relaxed">
                {isJa
                  ? "ハートが0になっても、そのままレッスンを続けられます。間違えた問題はあとで「復習便」でじっくり練習できるよ！"
                  : i18n.language === "id"
                    ? "Kamu tetap bisa melanjutkan pelajaran meskipun heart habis. Soal yang salah bisa dilatih lagi nanti di \"Paket Review\"!"
                    : "You can keep learning even with 0 hearts! Any missed questions will be delivered to your 'Review Parcel' to practice later."}
              </p>
              <div className="flex flex-col gap-2.5">
                <button
                  type="button"
                  onClick={refill}
                  className="w-full bg-primary text-white text-base font-bold py-3 rounded-2xl shadow hover:bg-primary/90 transition active:scale-98"
                >
                  {isJa
                    ? "このまま続ける（ハート全回復）"
                    : i18n.language === "id"
                      ? "Lanjut Belajar (Isi Penuh Heart)"
                      : "Keep Going (Full Hearts)"}
                </button>
                <Link
                  to="/"
                  className="w-full bg-stone-100 text-stone-600 text-sm font-bold py-2.5 rounded-2xl hover:bg-stone-200 transition"
                >
                  {isJa
                    ? "少しひと休み（マップへ戻る）"
                    : i18n.language === "id"
                      ? "Istirahat Dulu (Kembali ke Peta)"
                      : "Take a Break (Back to Map)"}
                </Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {quitOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              role="dialog"
              aria-modal="true"
              className="bg-white rounded-3xl shadow-2xl px-8 py-8 max-w-sm w-[90%] text-center"
            >
              <h2 className="text-xl font-bold text-primary mb-2">{t("learner:quit_title")}</h2>
              <p className="text-sm text-muted mb-6">{t("learner:quit_sub")}</p>
              <button
                onClick={() => setQuitOpen(false)}
                className="w-full bg-primary text-white text-lg font-bold py-3 rounded-2xl shadow hover:bg-primary/90 transition mb-3"
              >
                {t("learner:quit_keep")}
              </button>
              <button
                onClick={leaveToArea}
                className="w-full border-2 border-gray-300 text-muted font-bold py-2.5 rounded-2xl hover:bg-gray-50 transition"
              >
                {t("learner:quit_leave")}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── 解説（Skill Card）───────────────────────────────────────
function ExplanationScreen({
  explanation,
  titleJa,
  titleEn,
  onStart,
  onExit,
}: {
  explanation: LessonExplanation;
  titleJa: string;
  titleEn: string;
  onStart: () => void;
  onExit: () => void;
}) {
  const { t, i18n } = useTranslation(["common", "learner"]);
  const isJa = i18n.language === "ja";
  return (
    <div className="relative w-full h-screen overflow-hidden bg-gradient-to-br from-sakura/10 via-white to-blue-50 flex flex-col">
      <SakuraField />
      <header className="relative z-20 max-w-2xl mx-auto w-full px-4 sm:px-6 pt-4 sm:pt-6">
        <button
          onClick={onExit}
          aria-label={t("common:close")}
          className="bg-white/90 backdrop-blur rounded-full shadow-lg w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-primary text-lg sm:text-xl font-bold hover:bg-white"
        >
          ×
        </button>
      </header>

      <div className="relative z-20 flex-1 overflow-y-auto px-4 sm:px-6 py-5">
        <div className="w-full max-w-xl mx-auto">
          <div className="text-[11px] sm:text-xs text-muted tracking-wide">{t("learner:grammar_point")}</div>
          <h1 className="text-xl sm:text-2xl font-bold text-primary mb-3">{isJa ? titleJa : titleEn}</h1>

          <div className="bg-primary/10 rounded-2xl px-5 py-5 mb-4 text-center">
            <div className="text-2xl sm:text-3xl font-bold text-primary">{explanation.pattern}</div>
          </div>

          <p className="text-sm sm:text-base text-body leading-relaxed mb-5">{explanation.summary}</p>

          <div className="text-[11px] sm:text-xs text-muted mb-1.5">{t("learner:examples_label")}</div>
          <div className="flex flex-col gap-2 mb-5">
            {explanation.examples.map((ex, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm px-4 py-3">
                <div className="text-lg sm:text-xl font-bold text-primary">{ex.ja}</div>
                <div className="text-xs sm:text-sm text-muted mt-0.5">{ex.en}</div>
              </div>
            ))}
          </div>

          {explanation.tip && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-body">
              <span className="font-bold text-amber-700">{t("learner:tip_label")}</span>
              <span className="ml-2">{explanation.tip}</span>
            </div>
          )}
        </div>
      </div>

      <div className="relative z-20 border-t border-gray-200 bg-white/95 backdrop-blur">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <button
            onClick={onStart}
            className="w-full bg-primary text-white font-bold rounded-2xl py-3 shadow hover:bg-primary/90 transition text-base sm:text-lg"
          >
            {t("learner:lesson_start")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 穴埋めタスク ─────────────────────────────────────────────
function FillBlankView({
  task,
  choices,
  selected,
  feedback,
  onPick,
}: {
  task: Extract<Task, { type: "fill_blank" }>;
  choices: string[];
  selected: string | null;
  feedback: Feedback | null;
  onPick: (s: string) => void;
}) {
  const ok = feedback?.is_correct;
  let blankCls = "border-primary/40 text-muted";
  if (feedback) {
    blankCls = ok
      ? "border-green-500 text-green-700 bg-green-50"
      : "border-orange-400 text-orange-700 bg-orange-50";
  } else if (selected) {
    blankCls = "border-primary text-primary bg-primary/10";
  }
  return (
    <>
      {task.prompt && (
        <p className="text-base sm:text-lg text-body text-center mb-3 font-bold">{task.prompt}</p>
      )}
      <motion.div
        key={task.id}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.25 }}
        className="bg-white rounded-3xl shadow-2xl px-6 py-7 sm:px-10 sm:py-9 mb-6 sm:mb-8"
      >
        <div className="text-2xl sm:text-3xl font-bold text-primary leading-relaxed text-center flex flex-wrap items-center justify-center gap-x-1 gap-y-2">
          <span>{task.before}</span>
          <span className={`inline-flex min-w-[3.5rem] sm:min-w-[5rem] justify-center rounded border-b-4 px-2 pb-0.5 transition-colors ${blankCls}`}>
            {selected || "　"}
          </span>
          <span>{task.after}</span>
        </div>
      </motion.div>
      <ChoiceGrid choices={choices} answer={task.answer} selected={selected} feedback={feedback} onPick={onPick} />
    </>
  );
}

// ── 語彙タスク ───────────────────────────────────────────────
function VocabView({
  task,
  choices,
  selected,
  feedback,
  onPick,
}: {
  task: Extract<Task, { type: "vocab" }>;
  choices: string[];
  selected: string | null;
  feedback: Feedback | null;
  onPick: (s: string) => void;
}) {
  return (
    <>
      <motion.div
        key={task.id}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.25 }}
        className="bg-white rounded-3xl shadow-2xl px-8 py-7 sm:px-14 sm:py-10 mb-6 sm:mb-8 text-center"
      >
        <div className="text-5xl sm:text-7xl font-bold text-primary leading-none">{task.word}</div>
        {task.reading && <div className="mt-2 text-base sm:text-xl text-muted">{task.reading}</div>}
      </motion.div>
      <ChoiceGrid choices={choices} answer={task.answer} selected={selected} feedback={feedback} onPick={onPick} />
    </>
  );
}

// ── 文意タスク ───────────────────────────────────────────────
function TranslateView({
  task,
  choices,
  selected,
  feedback,
  onPick,
}: {
  task: Extract<Task, { type: "translate" }>;
  choices: string[];
  selected: string | null;
  feedback: Feedback | null;
  onPick: (s: string) => void;
}) {
  return (
    <>
      <motion.div
        key={task.id}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.25 }}
        className="bg-white rounded-3xl shadow-2xl px-6 py-7 sm:px-12 sm:py-9 mb-6 sm:mb-8 text-center"
      >
        <div className="text-2xl sm:text-4xl font-bold text-primary leading-snug">{task.sentence}</div>
      </motion.div>
      <ChoiceGrid choices={choices} answer={task.answer} selected={selected} feedback={feedback} onPick={onPick} stack />
    </>
  );
}

// 選択肢グリッド（vocab=2列、translate=1列）
function ChoiceGrid({
  choices,
  answer,
  selected,
  feedback,
  onPick,
  stack,
}: {
  choices: string[];
  answer: string;
  selected: string | null;
  feedback: Feedback | null;
  onPick: (s: string) => void;
  stack?: boolean;
}) {
  return (
    <div className={`grid gap-3 sm:gap-4 w-full ${stack ? "grid-cols-1" : "grid-cols-2"}`}>
      {choices.map((c, i) => {
        const isSelected = selected === c;
        const isAnswer = c === answer;
        let cls = "border-gray-200 hover:border-primary/60";
        let anim = {};

        if (feedback) {
          if (isAnswer) {
            cls = "border-green-500 bg-green-50/90 shadow-md text-green-900";
            anim = { scale: [1, 1.04, 1], y: [0, -4, 0] };
          } else if (isSelected) {
            cls = "border-orange-400 bg-orange-50/90 text-orange-900";
            anim = { x: [0, -6, 6, -4, 4, 0] };
          } else {
            cls = "border-gray-200 opacity-50";
          }
        } else if (isSelected) {
          cls = "border-primary bg-primary/10 shadow-lg";
        }

        return (
          <motion.button
            key={c}
            animate={anim}
            transition={{ duration: 0.3 }}
            whileHover={!feedback ? { scale: 1.02 } : {}}
            whileTap={!feedback ? { scale: 0.98 } : {}}
            disabled={!!feedback}
            onClick={() => onPick(c)}
            onMouseDown={(e) => e.preventDefault()}
            className={`relative bg-white border-2 rounded-2xl py-3.5 sm:py-4 px-4 text-base sm:text-lg font-bold text-body transition shadow-sm hover:shadow-md disabled:cursor-default focus:outline-none flex items-center justify-between ${cls}`}
          >
            <span className="flex-1 text-left">{c}</span>

            {/* 正解時のキラキラバッジ */}
            {feedback && isAnswer && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="text-base sm:text-lg"
              >
                ✨
              </motion.span>
            )}

            {/* PC向けキーボードショートカットバッジ [1]〜[4] */}
            {!feedback && (
              <span
                aria-hidden
                className="hidden md:flex px-1.5 py-0.5 rounded border border-stone-200 text-[10px] font-mono font-bold text-stone-400 bg-stone-50 shrink-0 ml-2"
              >
                {i + 1}
              </span>
            )}
          </motion.button>
        );
      })}
    </div>
  );
}

// ── 語順タスク ───────────────────────────────────────────────
function WordOrderView({
  task,
  bank,
  builtKeys,
  feedback,
  labelOf,
  onAdd,
  onRemove,
}: {
  task: Extract<Task, { type: "word_order" }>;
  bank: Array<{ key: number; label: string }>;
  builtKeys: number[];
  feedback: Feedback | null;
  labelOf: (k: number) => string;
  onAdd: (k: number) => void;
  onRemove: (k: number) => void;
}) {
  const used = new Set(builtKeys);
  const ok = feedback?.is_correct;
  return (
    <>
      <motion.div
        key={task.id}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.25 }}
        className="bg-white rounded-3xl shadow-xl px-6 py-5 sm:px-10 sm:py-7 mb-5 text-center"
      >
        <div className="text-lg sm:text-2xl font-bold text-body leading-snug">{task.prompt}</div>
      </motion.div>

      <div
        className={`min-h-[64px] sm:min-h-[76px] rounded-2xl border-2 border-dashed flex flex-wrap gap-2 items-center p-3 mb-5 transition-colors ${feedback ? (ok ? "border-green-400 bg-green-50" : "border-orange-400 bg-orange-50") : "border-primary/40 bg-white/70"
          }`}
      >
        {builtKeys.map((k) => (
          <motion.button
            key={k}
            layout
            disabled={!!feedback}
            onClick={() => onRemove(k)}
            className="bg-primary text-white font-bold rounded-xl px-3.5 py-2 text-base sm:text-lg shadow disabled:opacity-90"
          >
            {labelOf(k)}
          </motion.button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 sm:gap-3 justify-center">
        {bank.map((b) => (
          <motion.button
            key={b.key}
            layout
            whileTap={!feedback ? { scale: 0.95 } : {}}
            disabled={used.has(b.key) || !!feedback}
            onClick={() => onAdd(b.key)}
            className={`font-bold rounded-xl px-3.5 py-2 text-base sm:text-lg border-2 transition ${used.has(b.key)
                ? "border-gray-200 bg-gray-100 text-transparent shadow-inner"
                : "border-gray-200 bg-white text-body shadow-sm hover:border-primary/60"
              }`}
          >
            {b.label}
          </motion.button>
        ))}
      </div>
    </>
  );
}

// ── フィードバックバー ───────────────────────────────────────
function FeedbackBar({
  feedback,
  submitting,
  isLast,
  onNext,
  onReport,
}: {
  feedback: Feedback;
  submitting: boolean;
  isLast: boolean;
  onNext: () => void;
  onReport: () => void;
}) {
  const { t } = useTranslation(["common", "learner"]);
  const ok = feedback.is_correct;
  return (
    <div className="flex items-center justify-between w-full gap-2 sm:gap-4">
      <div className="flex-1 min-w-0">
        <div className={`text-base sm:text-xl font-bold ${ok ? "text-green-700" : "text-orange-700"} flex items-center gap-2 flex-wrap`}>
          <span className={`inline-flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 rounded-full ${ok ? "bg-green-600" : "bg-orange-500"} text-white text-xs sm:text-base shrink-0`}>
            {ok ? "✓" : "✗"}
          </span>
          <span>{ok ? t("learner:correct") : t("learner:incorrect")}</span>
          {!ok && (
            <span className="text-sm sm:text-base text-body font-normal">
              {t("learner:answer_is")} <strong className="text-primary">{feedback.correct_answer}</strong>
            </span>
          )}
        </div>
        <button
          onClick={onReport}
          className={`mt-1 inline-flex items-center gap-1 text-[10px] sm:text-xs font-bold tracking-wide uppercase hover:underline ${ok ? "text-green-700" : "text-orange-700"}`}
        >
          <FlagIcon />
          <span>{t("learner:report")}</span>
        </button>
      </div>
      <button
        onClick={onNext}
        disabled={submitting}
        className={`font-bold rounded-xl sm:rounded-2xl px-6 sm:px-10 py-2.5 sm:py-3 shadow text-white text-sm sm:text-base shrink-0 disabled:opacity-60 ${ok ? "bg-green-600 hover:bg-green-700" : "bg-orange-500 hover:bg-orange-600"}`}
      >
        {submitting ? t("learner:checking") : isLast ? t("learner:finish") : t("learner:next")}
      </button>
    </div>
  );
}

// ── ユニット完了画面 ─────────────────────────────────────────
function UnitComplete({
  result,
  section,
  unitIndex,
  onRetry,
}: {
  result: UnitResult;
  section: Section;
  unitIndex: number;
  onRetry: () => void;
}) {
  const { t, i18n } = useTranslation(["common", "learner"]);
  const isJa = i18n.language === "ja";
  const hasNextUnit = unitIndex < UNITS_PER_SECTION && !result.section_cleared;
  const mapArea = AREA_BY_ID[section.area_id as AreaId];

  // 住民のお祝いセリフを取得
  const congrats = mapArea ? RESIDENT_CONGRATS[mapArea.id] : undefined;

  return (
    <div className="relative w-full min-h-screen bg-gradient-to-br from-[#fff7e6] via-white to-[#dcece8] flex flex-col items-center justify-center px-4 py-8 overflow-y-auto">
      {/* 祝福動画・アニメーションセレブレーション */}
      <CelebrationVideo area={mapArea} isJa={isJa} />
      <SakuraField />

      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", damping: 15 }}
        className="relative z-10 mb-2 text-center"
      >
        <span className="text-5xl select-none animate-pulse">🎉</span>
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="relative z-10 text-3xl sm:text-4xl font-black text-[#273f7a] mb-1 text-center tracking-tight"
      >
        {t("learner:unit_clear")}
      </motion.h1>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.25, duration: 0.4 }}
        className="relative z-10 text-xs sm:text-sm text-stone-500 mb-4 text-center font-bold"
      >
        {t("learner:section_progress", { done: result.section_done, total: result.section_total })}
      </motion.p>

      {/* できたこと（成功体験コピーの強調表示） */}
      {mapArea && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.4 }}
          className="relative z-10 w-full max-w-md mb-4 bg-white/95 backdrop-blur-md rounded-2xl shadow-md border-2 border-[#d9b45f]/40 p-4 text-center"
        >
          <div className="text-[10px] sm:text-xs font-black tracking-widest text-[#8f332c] uppercase mb-1">
            {isJa ? "できるようになったこと" : "What you can do now"}
          </div>
          <p className="text-base sm:text-lg font-black text-[#273f7a] leading-relaxed">
            {isJa ? mapArea.clearCopyJa : mapArea.clearCopyEn}
          </p>
        </motion.div>
      )}

      {/* 住民からの褒め言葉（どうぶつ風AnimalBubble） */}
      {mapArea && congrats && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.45, duration: 0.35 }}
          className="relative z-10 w-full max-w-md mb-5"
        >
          <AnimalBubble
            area={mapArea}
            isJa={isJa}
            message={isJa ? congrats.ja : congrats.en}
            size="md"
          />
        </motion.div>
      )}

      {/* 報酬・統計カード（動くアイコン付き） */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55, duration: 0.4 }}
        className="relative z-10 grid grid-cols-3 gap-2.5 sm:gap-4 w-full max-w-md mb-5"
      >
        <div className="bg-white/90 backdrop-blur rounded-2xl p-3 border border-yellow-200/80 shadow-sm flex flex-col items-center">
          <div className="flex items-center gap-1 mb-0.5">
            <SparkleStarIcon size={18} />
            <span className="text-[10px] sm:text-xs text-stone-500 font-bold">{t("learner:earned_xp_label")}</span>
          </div>
          <span className="text-base sm:text-lg font-black text-yellow-600">+{result.xp_awarded}</span>
        </div>

        <div className="bg-white/90 backdrop-blur rounded-2xl p-3 border border-pink-200/80 shadow-sm flex flex-col items-center">
          <div className="flex items-center gap-1 mb-0.5">
            <SakuraCoinIcon size={18} />
            <span className="text-[10px] sm:text-xs text-stone-500 font-bold">{t("learner:coin_label")}</span>
          </div>
          <span className="text-base sm:text-lg font-black text-pink-600">+{result.coin_awarded}</span>
        </div>

        <Stat
          label={t("learner:accuracy")}
          value={`${result.accuracy}%`}
          accent="text-green-600"
          sub={`${result.correct_count}/${result.total}`}
        />
      </motion.div>

      {(result.section_just_cleared || result.area_just_cleared) && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          className="relative z-10 w-full max-w-md mb-5 bg-white rounded-2xl shadow-lg border-2 border-green-200 px-5 py-4 text-center"
        >
          <div className="text-green-700 font-bold text-base sm:text-lg mb-1">
            {result.area_just_cleared ? t("learner:area_cleared") : t("learner:section_cleared")}
          </div>
          {result.area_just_cleared && result.newly_unlocked_areas.length > 0 && (
            <div className="text-sm text-body">
              {t("learner:area_unlocked")}：
              <span className="font-bold text-primary">
                {result.newly_unlocked_areas.map((a) => (isJa ? a.name_ja : a.name_en)).join(" / ")}
              </span>
            </div>
          )}
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.65, duration: 0.4 }}
        className="relative z-10 w-full max-w-md flex flex-col gap-2 sm:gap-3"
      >
        {hasNextUnit ? (
          <Link
            to={`/unit/${section.id}/${unitIndex + 1}`}
            replace // 終わったユニットの画面を履歴に残さない（戻るで同じユニットが最初から始まらないように）
            className="w-full bg-primary text-white font-bold py-3 rounded-2xl shadow hover:bg-primary/90 transition text-base sm:text-lg text-center"
          >
            {t("learner:next_unit")}
          </Link>
        ) : (
          <Link
            to={`/area/${section.area_id}`}
            className="w-full bg-primary text-white font-bold py-3 rounded-2xl shadow hover:bg-primary/90 transition text-base sm:text-lg text-center"
          >
            {t("learner:back_to_area")}
          </Link>
        )}
        <button
          onClick={onRetry}
          className="w-full bg-white border-2 border-gray-200 text-primary font-bold py-3 rounded-2xl shadow-sm hover:border-primary/60 transition text-base sm:text-lg"
        >
          {t("learner:retry_lesson")}
        </button>
        {hasNextUnit && (
          <Link
            to={`/area/${section.area_id}`}
            className="w-full text-muted font-bold py-2 text-center text-sm hover:text-primary transition"
          >
            {t("learner:back_to_area")}
          </Link>
        )}
      </motion.div>
    </div>
  );
}

function Stat({ label, value, accent, sub }: { label: string; value: string; accent: string; sub?: string }) {
  return (
    <div className="bg-white rounded-2xl shadow-md px-2 sm:px-3 py-3 sm:py-4 flex flex-col items-center text-center">
      <div className={`text-lg sm:text-2xl font-bold ${accent} leading-tight`}>{value}</div>
      {sub && <div className="text-[10px] sm:text-xs text-muted leading-tight">{sub}</div>}
      <div className="text-[10px] sm:text-xs text-muted mt-1">{label}</div>
    </div>
  );
}

function buildLocalUnitResult(
  section: Section,
  unitIndex: number,
  answers: Array<{ task_id: string; selected: string }>,
  correctCount: number,
): UnitResult {
  const total = answers.length;
  return {
    unit_id: `${section.id}-u${unitIndex}`,
    section_id: section.id,
    area_id: section.area_id,
    unit_index: unitIndex,
    total,
    correct_count: correctCount,
    accuracy: total > 0 ? Math.round((correctCount / total) * 100) : 0,
    xp_awarded: correctCount * 5,
    coin_awarded: correctCount * 5,
    is_first_clear: false,
    section_done: 0,
    section_total: UNITS_PER_SECTION,
    section_cleared: false,
    section_just_cleared: false,
    area_just_cleared: false,
    newly_unlocked_areas: [],
    results: answers.map((a) => ({ task_id: a.task_id, is_correct: false, correct_answer: "" })),
  };
}

function HeartIcon({ filled, large }: { filled: boolean; large?: boolean }) {
  const size = large ? "w-16 h-16" : "w-6 h-6";
  return (
    <svg viewBox="0 0 24 24" className={size} fill={filled ? "#C04A4A" : "none"} stroke="#C04A4A" strokeWidth="2">
      <path d="M12 21s-7-4.5-9.5-9C0.5 8 3 4 6.5 4c2 0 3.5 1.5 5.5 3.5C14 5.5 15.5 4 17.5 4 21 4 23.5 8 21.5 12c-2.5 4.5-9.5 9-9.5 9z" strokeLinejoin="round" />
    </svg>
  );
}

function FlagIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 21V4M4 4l13 4-3 4 3 4-13 0" />
    </svg>
  );
}
